// Shared helpers for split-week rotation rendering.
// Used by SplitWeekSchedule (interactive), SplitWeekDisplay (read-only), and
// WeeklyRotationSummary (aggregate). All helpers are defensive — they tolerate
// null/undefined/malformed data from the backend and normalize to safe shapes.

export const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

// Backend contract uses day_of_week 0=Mon..6=Sun on DayRotationInput
// (see distrohub/use_cases/brs/create.py and update.py). Keep this mapping
// here so the UI's {mon,tue,...} schedule object can be translated in either
// direction without scattering the ordering across files.
const DAY_KEY_TO_INDEX = { mon: 0, tue: 1, wed: 2, thu: 3, fri: 4, sat: 5, sun: 6 };
const DAY_INDEX_TO_KEY = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Clamp any numeric input to an integer in [0, 100]. The old version snapped
// to {0,25,50,100}; we now accept any integer per cell so operators can dial
// in arbitrary rotation splits (e.g. 33% / 67%) directly in the summary.
const clampPct = (n) => {
  if (!Number.isFinite(n)) return 0;
  const i = Math.round(n);
  if (i <= 0) return 0;
  if (i >= 100) return 100;
  return i;
};

// Unit-mode clamp: only enforce non-negative (no upper bound). Units are
// absolute counts, not percentages, so values above 100 are perfectly valid.
const clampUnit = (n) => {
  if (!Number.isFinite(n)) return 0;
  const i = Math.round(n);
  return i < 0 ? 0 : i;
};

/**
 * Normalize a raw split_week_schedule from the backend into a safe, fully-populated
 * object with integer values in [0, 100]. Missing keys default to 100.
 * Accepts: null, undefined, partial objects, string values ("50"), etc.
 */
export const normalizeSchedule = (raw) => {
  const out = {};
  const source = raw && typeof raw === 'object' ? raw : {};
  for (const d of DAYS) {
    const v = source[d.key];
    if (v === undefined || v === null || v === '') {
      out[d.key] = 100;
    } else {
      out[d.key] = clampPct(Number(v));
    }
  }
  return out;
};

/**
 * Mode-aware variant of normalizeSchedule. In unit mode the default for
 * missing keys is 1 (one unit) and values are not clamped to 100. In
 * rotation mode it delegates to the original normalizeSchedule.
 */
export const normalizeScheduleForMode = (raw, unitRotation) => {
  if (unitRotation !== 'unit') return normalizeSchedule(raw);
  const out = {};
  const source = raw && typeof raw === 'object' ? raw : {};
  for (const d of DAYS) {
    const v = source[d.key];
    if (v === undefined || v === null || v === '') {
      out[d.key] = 1;
    } else {
      out[d.key] = clampUnit(Number(v));
    }
  }
  return out;
};

/**
 * Returns true if the schedule contains any non-100 day, meaning the operator
 * configured a real split-week. A schedule of all 100% is functionally identical
 * to uniform rotation and should be rendered as such.
 */
export const hasSplitWeekData = (raw) => {
  if (!raw || typeof raw !== 'object') return false;
  const normalized = normalizeSchedule(raw);
  return DAYS.some((d) => normalized[d.key] !== 100);
};

// Bucket arbitrary integer percentages into existing visual classes so the
// dayCell* CSS continues to apply even though cells are now free-form inputs.
export const pctClass = (classes, value) => {
  const v = clampPct(Number(value));
  if (v >= 75) return classes.dayCellPct100;
  if (v >= 40) return classes.dayCellPct50;
  if (v > 0) return classes.dayCellPct25;
  return classes.dayCellPct0;
};

export const displayPct = (value) => {
  const v = clampPct(Number(value));
  return v === 0 ? '\u2014' : `${v}%`;
};

// Parse a free-form cell edit ("", "50", "abc") into a clamped integer.
// Returns null when input cannot be coerced so callers can keep the previous
// value rather than silently flipping to 0 mid-edit.
export const parseCellInput = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return 0;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return clampPct(n);
};

/**
 * Mode-aware variant of parseCellInput. In unit mode values are not clamped
 * to 100 — units are absolute counts, not percentages.
 */
export const parseCellInputForMode = (raw, unitRotation) => {
  if (unitRotation !== 'unit') return parseCellInput(raw);
  if (raw === '' || raw === null || raw === undefined) return 0;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return clampUnit(n);
};

/**
 * Per-day column totals across every spot's split-week schedule.
 * Returns { mon: N, tue: N, ... } where each value is the integer sum of
 * that day's percent across all provided spots.
 */
export const computeDailyTotals = (spots) => {
  const totals = { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 };
  if (!Array.isArray(spots)) return totals;
  for (const spot of spots) {
    const s = normalizeSchedule(spot?.split_week_schedule);
    for (const d of DAYS) totals[d.key] += s[d.key];
  }
  return totals;
};

/**
 * Rotation-mode gate for Submit / Generate PDF. Each day column must sum to
 * exactly 100% across all spots. Returns true in unit mode (no constraint).
 * Empty spot list in rotation mode is invalid (0 ≠ 100).
 *
 * NOTE: the strict "every day === 100" requirement was retired — a day column
 * is now allowed to total 0% (nothing airing), which the operator confirms at
 * submit time (see getZeroTotalDays). The cross-spot "≤ 100% per day" guard
 * (validateDayRotationTotals in BrsCreate) is the surviving hard rule. This
 * helper is kept for any callers that still want the legacy check.
 */
export const isRotationTotalsValid = (spots, unitRotation) => {
  if (unitRotation !== 'rotation') return true;
  if (!Array.isArray(spots) || spots.length === 0) return false;
  const totals = computeDailyTotals(spots);
  return DAYS.every((d) => totals[d.key] === 100);
};

/**
 * Return the DAYS entries whose cross-spot column total is exactly 0% — i.e.
 * no spot airs that day. Used to prompt the operator for explicit confirmation
 * before submitting a schedule that leaves one or more days empty (the PDF
 * generator renders a "Nothing airing on X" note for these days).
 */
export const getZeroTotalDays = (spots) => {
  if (!Array.isArray(spots) || spots.length === 0) return [];
  const totals = computeDailyTotals(spots);
  return DAYS.filter((d) => totals[d.key] === 0);
};

/**
 * Compute the Mon-Sun short date labels (M/D) for the week containing the given
 * start date. Accepts a Date, a "YYYY-MM-DD" string, or a full ISO timestamp.
 * Returns 7 empty strings if the input is missing or unparseable.
 */
/**
 * Convert a UI split_week_schedule object ({mon:100,tue:50,...}) into the
 * DayRotationInput[] array shape the backend accepts on
 * BroadcastCreateRequest.day_rotations / BroadcastUpdateRequest.day_rotations.
 *
 * Uniform-100 schedules emit an empty array: the backend treats a missing
 * day_rotations list as "no split" and cross-spot validation sums to 100 per
 * day, so sending explicit 100s for every day of a single spot would still
 * pass but is noise. Returning [] keeps the wire format aligned with how the
 * operator actually filled out the form.
 */
export const scheduleToDayRotations = (schedule) => {
  if (!hasSplitWeekData(schedule)) return [];
  const normalized = normalizeSchedule(schedule);
  return DAYS.map((d) => ({
    day_of_week: DAY_KEY_TO_INDEX[d.key],
    percent: normalized[d.key],
  }));
};

/**
 * Inverse of scheduleToDayRotations: turn a backend DayRotation[] array into
 * the UI's {mon,tue,...} object. Missing days default to 100 (uniform).
 * Used when pre-populating a form from a prior BR.
 */
export const dayRotationsToSchedule = (dayRotations) => {
  const out = { mon: 100, tue: 100, wed: 100, thu: 100, fri: 100, sat: 100, sun: 100 };
  if (!Array.isArray(dayRotations)) return out;
  for (const dr of dayRotations) {
    if (!dr || dr.day_of_week === undefined || dr.day_of_week === null) continue;
    const key = DAY_INDEX_TO_KEY[Number(dr.day_of_week)];
    if (!key) continue;
    out[key] = clampPct(Number(dr.percent));
  }
  return out;
};

export const computeWeekDates = (startDate) => {
  const empty = Array(7).fill('');
  if (!startDate) return empty;

  let d;
  if (startDate instanceof Date) {
    d = new Date(startDate.getTime());
  } else {
    const dateStr = String(startDate).slice(0, 10);
    const parts = dateStr.split('-');
    if (parts.length !== 3) return empty;
    d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  }
  if (Number.isNaN(d.getTime())) return empty;

  const dow = (d.getDay() + 6) % 7; // Mon=0 ... Sun=6
  const monday = new Date(d);
  monday.setDate(d.getDate() - dow);
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    return `${day.getMonth() + 1}/${day.getDate()}`;
  });
};
